import { useMemo } from 'react'
import {
  crewEntriesFromSlots,
  crewEntryForPosition,
  crewPositionLabels,
  crewPositions,
  crewSlotsFromEntries,
  type CrewPosition,
} from '@@api/flightLog.shared'
import { isPilotRole } from '@@api/shared'
import type { FlightLogLegCrewMember, FlightLogSectionContext } from '../useFlightLogForm'
import { PrntrGroup, PrntrSelect } from '../../ui/prntr-input'

const NONE = '__none__'

// Which rostered members a position may be filled from
const eligibleForPosition = (position: CrewPosition, member: FlightLogLegCrewMember) => {
  const isPilot = isPilotRole(member.crewRole)
  const isEngineer = member.crewRole === 'Engineer'
  switch (position) {
    case 'PF':
    case 'PM':
    case 'AUGMENTED':
      return isPilot
    case 'FE':
      return isEngineer
    default:
      return !isPilot && !isEngineer
  }
}

export function CrewSection({ context }: { context: FlightLogSectionContext }) {
  const { values, setValue, readOnly, fieldError, legCrew } = context

  // Fixed position rows (PF → PM → augmented → F/A 1 → F/A 2 → engineer);
  // each is a view over routeTimes.crew. Crew membership itself is managed
  // on the flight — only the assignment happens here.
  const slots = useMemo(() => crewSlotsFromEntries(values.routeTimes.crew), [values.routeTimes.crew])

  const assign = (position: CrewPosition, crewId: string) => {
    const next = { ...slots }
    if (crewId === NONE) {
      next[position] = null
    } else {
      const member = legCrew.find((entry) => entry.crewId === crewId)
      if (!member) return
      // A member holds one position at a time
      for (const other of crewPositions) {
        if (other !== position && next[other]?.crewId === crewId) next[other] = null
      }
      next[position] = crewEntryForPosition(position, member)
    }
    setValue('routeTimes.crew', crewEntriesFromSlots(next), { shouldDirty: true })
  }

  return (
    <>
      <PrntrGroup>
        {crewPositions.map((position) => {
          const current = slots[position]
          const options = [
            { value: NONE, label: 'None' },
            ...legCrew
              .filter((member) => eligibleForPosition(position, member))
              .filter((member) => {
                const holder = crewPositions.find((p) => slots[p]?.crewId === member.crewId)
                return !holder || holder === position
              })
              .map((member) => ({ value: member.crewId, label: member.name })),
          ]
          return (
            <PrntrSelect
              key={position}
              label={crewPositionLabels[position]}
              value={current?.crewId ?? NONE}
              onChange={(next) => assign(position, next)}
              options={options}
              display={current ? current.name : '—'}
              valueTone={current ? 'info' : 'default'}
              readOnly={readOnly}
            />
          )
        })}
      </PrntrGroup>
      {legCrew.length === 0 && (
        <p className="text-xs text-content-primary-subtle">No crew assigned to this flight yet.</p>
      )}
      {fieldError('routeTimes.crew') && (
        <p className="text-xs text-content-negative-bold">{fieldError('routeTimes.crew')}</p>
      )}
    </>
  )
}
